import { useId } from 'react'
import type { ChatChartSpec } from './chart-spec'

const number = (value: number) => new Intl.NumberFormat('fa-IR', { maximumSignificantDigits: 6 }).format(value)
const tick = (value: number) => new Intl.NumberFormat('fa-IR', { notation: 'compact', maximumSignificantDigits: 3 }).format(value)

/** Local SVG and React text only. No scripts, remote images, or model-supplied markup. */
export function ChatChart({ spec }: { spec: ChatChartSpec }) {
  const titleId = useId()
  return (
    <figure className="chat-chart" aria-labelledby={titleId}>
      <figcaption id={titleId} className="chat-chart__caption">
        <strong>{spec.title}</strong>
        {spec.type !== 'flow' && spec.unit && <span>({spec.unit})</span>}
        {spec.illustrative && <span className="badge badge--generated">مثال فرضی</span>}
      </figcaption>
      {spec.type === 'flow' ? (
        <ol className="chat-chart__flow">
          {spec.steps.map((step, index) => <li key={index}>{step}</li>)}
        </ol>
      ) : (
        <>
          <NumericChart spec={spec} />
          <details className="chat-chart__data">
            <summary>نمایش داده‌ها</summary>
            <div className="table-scroll" tabIndex={0} role="region" aria-label={spec.title}>
              <table className="table">
                <caption>{spec.title}{spec.unit && ` (${spec.unit})`}</caption>
                <thead><tr><th scope="col">عنوان</th><th scope="col">مقدار{spec.unit && ` (${spec.unit})`}</th></tr></thead>
                <tbody>{spec.data.map((d, index) => <tr key={index}><th scope="row">{d.label}</th><td>{number(d.value)}</td></tr>)}</tbody>
              </table>
            </div>
          </details>
        </>
      )}
      {spec.source && <p className="chat-chart__source">منبع: {spec.source}</p>}
    </figure>
  )
}

function NumericChart({ spec }: { spec: Extract<ChatChartSpec, { type: 'bar' | 'line' }> }) {
  const low = Math.min(0, ...spec.data.map((d) => d.value))
  const high = Math.max(0, ...spec.data.map((d) => d.value))
  const span = high - low || 1
  if (spec.type === 'bar') {
    const zero = ((0 - low) / span) * 100
    return <div className="chat-chart__bars" role="list" aria-label={spec.title}>
      {spec.data.map((d, index) => <div className="chat-chart__bar-row" role="listitem" key={index}>
        <span>{d.label}</span>
        <div className="chat-chart__track" aria-hidden="true">
          <span className="chat-chart__zero" style={{ left: `${zero}%` }} />
          <span className="chat-chart__fill" style={{ left: `${((Math.min(0, d.value) - low) / span) * 100}%`, width: `${(Math.abs(d.value) / span) * 100}%` }} />
        </div>
        <span className="chat-chart__value">{number(d.value)}{spec.unit && ` ${spec.unit}`}</span>
      </div>)}
    </div>
  }
  const x = (index: number) => 58 + (spec.data.length === 1 ? 250 : (index / (spec.data.length - 1)) * 500)
  const y = (value: number) => 174 - ((value - low) / span) * 150
  return <div className="chat-chart__plot" role="region" tabIndex={0} aria-label={spec.title}>
    <svg viewBox="0 0 580 210" role="img" aria-label={`${spec.title}${spec.unit ? ` (${spec.unit})` : ''}`}>
      <title>{spec.title}</title>
      {[0, 0.5, 1].map((fraction) => {
        const value = low + fraction * span
        return <g key={fraction}>
          <line x1={58} x2={558} y1={y(value)} y2={y(value)} className="chat-chart__grid" />
          <text x={50} y={y(value) + 4} textAnchor="end">{tick(value)}</text>
        </g>
      })}
      <polyline points={spec.data.map((d, index) => `${x(index)},${y(d.value)}`).join(' ')} className="chat-chart__line" />
      {spec.data.map((d, index) => <circle key={index} cx={x(index)} cy={y(d.value)} r={4} className="chat-chart__dot"><title>{`${d.label}: ${number(d.value)}`}</title></circle>)}
    </svg>
    <div className="chat-chart__ends"><span>{spec.data[0]?.label}</span><span>{spec.data.at(-1)?.label}</span></div>
  </div>
}
